import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { inventoryAPI, requestsAPI, invoicesAPI } from '../../services/api';
import { useQuery } from 'react-query';
import {
  LuArrowDownToLine, LuArrowUpFromLine, LuClipboardList, LuFileText,
  LuPlus, LuClock, LuCalendar, LuPackage, LuBox
} from 'react-icons/lu';
import { formatDate, formatStatus } from '../../utils/formatters';
import LoadingSpinner from '../../components/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();

  // Fetch enhanced dashboard data
  const { data: dashboardData, isLoading, error } = useQuery(
    ['customerDashboard'],
    async () => {
      // Use Promise.allSettled so one failed API call does not blank out the entire dashboard
      const [requestsResult, inventoryResult, invoicesResult] = await Promise.allSettled([
        requestsAPI.getRequests({ limit: 100 }),
        inventoryAPI.getInventory({ limit: 100 }),
        invoicesAPI.getInvoices({ limit: 100 })
      ]);

      const requests = requestsResult.status === 'fulfilled' ? (requestsResult.value.data || []) : [];
      const inventory = inventoryResult.status === 'fulfilled' ? (inventoryResult.value.data || []) : [];
      const invoices = invoicesResult.status === 'fulfilled' ? (invoicesResult.value.data || []) : [];

      // Calculate metrics
      const now = new Date();
      const twoDaysFromNow = new Date(now.getTime() + (48 * 60 * 60 * 1000));

      const pendingInbound = requests.filter(r =>
        ['incoming', 'inbound_manifest'].includes(r.requestType) && ['pending', 'approved', 'pending_receive'].includes(r.status)
      ).length;

      const pendingOutbound = requests.filter(r =>
        ['ship', 'pick_pack'].includes(r.requestType) && ['pending', 'approved', 'in_progress'].includes(r.status)
      ).length;

      const pendingKits = requests.filter(r =>
        r.requestType === 'kit_build' && ['pending', 'approved', 'in_progress'].includes(r.status)
      ).length;

      const unpaidInvoices = invoices.filter(i => i.status === 'pending').length;

      // Upcoming requests (within 48 hours)
      const upcomingRequests = requests.filter(r => {
        const scheduledDate = new Date(r.scheduled_date || r.scheduledDate);
        return scheduledDate >= now && scheduledDate <= twoDaysFromNow &&
               ['pending', 'approved'].includes(r.status);
      }).slice(0, 5);

      // In progress requests
      const inProgressRequests = requests.filter(r =>
        ['in_progress', 'pending_receive', 'partially_received'].includes(r.status)
      ).slice(0, 5);

      // Recent requests (last 10)
      const recentRequests = requests
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10);

      // Inventory summary
      const totalInventoryItems = inventory.length;
      const totalUnits = inventory.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);

      // Low stock items (quantity at or below reorder threshold)
      const lowStockItems = inventory.filter(item => {
        const qty = parseInt(item.quantity) || 0;
        const threshold = parseInt(item.reorderThreshold) || 0;
        return threshold > 0 && qty <= threshold;
      }).slice(0, 5);

      return {
        metrics: {
          pendingInbound,
          pendingOutbound,
          pendingKits,
          unpaidInvoices
        },
        upcomingRequests,
        inProgressRequests,
        recentRequests,
        lowStockItems,
        inventory: {
          totalItems: totalInventoryItems,
          totalUnits
        }
      };
    },
    {
      staleTime: 60000, // 1 minute
      refetchInterval: 300000, // 5 minutes
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error loading dashboard data. Please try again.</div>
      </div>
    );
  }

  const { metrics, upcomingRequests, inProgressRequests, recentRequests, inventory } = dashboardData || {};

  const getTypeBadgeClasses = (requestType) => {
    switch (requestType) {
      case 'incoming': return 'bg-blue-100 text-blue-800';
      case 'inbound_manifest': return 'bg-indigo-100 text-indigo-800';
      case 'ship': return 'bg-green-100 text-green-800';
      case 'pick_pack': return 'bg-green-100 text-green-800';
      case 'crossdock': return 'bg-purple-100 text-purple-800';
      case 'kit_build': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending_receive': return 'bg-yellow-100 text-yellow-800';
      case 'partially_received': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome back, {user?.name?.split(' ')[0] || 'there'}</p>
          </div>
          <Link
            to="/customer/requests/new"
            className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <LuPlus className="mr-2 h-4 w-4" />
            New Request
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-4">

          {/* Current Status */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Current Status</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Link to="/customer/requests?type=incoming&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuArrowDownToLine className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Inbound</p>
                      <p className="text-2xl font-semibold text-gray-900">{metrics?.pendingInbound || 0}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/customer/requests?type=ship&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuArrowUpFromLine className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Outbound</p>
                      <p className="text-2xl font-semibold text-gray-900">{metrics?.pendingOutbound || 0}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/customer/requests?type=kit_build&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuClipboardList className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Kits</p>
                      <p className="text-2xl font-semibold text-gray-900">{metrics?.pendingKits || 0}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/customer/invoices?status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuFileText className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Unpaid Invoices</p>
                      <p className="text-2xl font-semibold text-gray-900">{metrics?.unpaidInvoices || 0}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Two Column Layout for Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upcoming Requests (Next 48 Hours) */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <LuCalendar className="mr-2 h-5 w-5" />
                  Upcoming Requests (48hrs)
                </h2>
                <Link to="/customer/requests" className="text-sm text-blue-600 hover:text-blue-500">
                  View all
                </Link>
              </div>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                {upcomingRequests && upcomingRequests.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {upcomingRequests.map((request) => (
                      <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClasses(request.requestType)}`}>
                                {request.requestType.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {request.requestNumber || `${request.requestType.toUpperCase()}-${request.id.slice(-6)}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {formatDate(request.scheduled_date || request.scheduledDate)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(request.status)}`}>
                              {formatStatus(request.status, request.requestType)}
                            </span>
                            <Link
                              to={`/customer/requests/${request.id}`}
                              className="block text-sm text-blue-600 hover:text-blue-500 mt-1"
                            >
                              View details
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    <LuCalendar className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2">No upcoming requests in the next 48 hours</p>
                  </div>
                )}
              </div>
            </div>

            {/* Work in Progress */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <LuClock className="mr-2 h-5 w-5" />
                  In Progress
                </h2>
                <Link to="/customer/requests?status=in_progress" className="text-sm text-blue-600 hover:text-blue-500">
                  View all
                </Link>
              </div>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                {inProgressRequests && inProgressRequests.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {inProgressRequests.map((request) => (
                      <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClasses(request.requestType)}`}>
                                {request.requestType.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {request.requestNumber || `${request.requestType.toUpperCase()}-${request.id.slice(-6)}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {formatDate(request.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              In Progress
                            </span>
                            <Link
                              to={`/customer/requests/${request.id}`}
                              className="block text-sm text-blue-600 hover:text-blue-500 mt-1"
                            >
                              View details
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    <LuClock className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2">No requests currently in progress</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Requests */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Recent Requests</h2>
              <Link to="/customer/requests" className="text-sm text-blue-600 hover:text-blue-500">
                View all requests
              </Link>
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              {recentRequests && recentRequests.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {recentRequests.slice(0, 8).map((request) => (
                    <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClasses(request.requestType)}`}>
                              {request.requestType.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              {request.requestNumber || `${request.requestType.toUpperCase()}-${request.id.slice(-6)}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(request.status)}`}>
                            {formatStatus(request.status, request.requestType)}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(request.createdAt)}
                          </p>
                          <Link
                            to={`/customer/requests/${request.id}`}
                            className="text-sm text-blue-600 hover:text-blue-500"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-6 text-center text-gray-500">
                  <LuClipboardList className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-2">No recent requests</p>
                  <Link
                    to="/customer/requests/new"
                    className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                  >
                    <LuPlus className="mr-1 h-4 w-4" />
                    Create your first request
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Inventory Summary */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <LuBox className="mr-2 h-5 w-5" />
                Inventory Summary
              </h2>
              <Link to="/customer/inventory" className="text-sm text-blue-600 hover:text-blue-500">
                View inventory
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuPackage className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Total SKUs</p>
                      <p className="text-2xl font-semibold text-gray-900">{inventory?.totalItems || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuBox className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Total Units</p>
                      <p className="text-2xl font-semibold text-gray-900">{inventory?.totalUnits || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Low Stock Alert */}
          {dashboardData?.lowStockItems?.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-yellow-800">Low Stock Items</h2>
                <Link to="/customer/inventory?status=low-stock" className="text-sm text-yellow-700 hover:text-yellow-900">
                  View all
                </Link>
              </div>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-yellow-200">
                <ul className="divide-y divide-gray-200">
                  {dashboardData.lowStockItems.map(item => (
                    <li key={item.id}>
                      <Link to={`/customer/inventory/${item.id}`} className="flex items-center justify-between px-4 py-4 hover:bg-gray-50">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.productSku}</p>
                          <p className="text-sm text-gray-500">{item.productName}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-red-600">{item.quantity} units</p>
                          <p className="text-xs text-gray-400">Reorder at {item.reorderThreshold}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
